import { DS } from '@/design-system'
import { useStore } from '@/models'
import { useTabNavConfig } from '@/hooks/useTabNavConfig'
import { Avatar } from '@/screens/profile-screen/edit/AvatarBuilder'
import { TabRoutes } from '@/navigation/tab-routes'
// import { BottomTabBarProps } from '@react-navigation/bottom-tabs'
import { useFocusEffect } from 'expo-router'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { LayoutChangeEvent, Pressable, StyleSheet, useWindowDimensions, View } from 'react-native'
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'

import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'

import HomeActiveIcon from '@/assets/tabNavigator/Home-active .svg'
import HomeInactiveIcon from '@/assets/tabNavigator/Home-inactive.svg'
import LearnFilledIcon from '@/assets/tabNavigator/Learn--filled.svg'
import LearnIcon from '@/assets/tabNavigator/Learn.svg'
import PlayActiveIcon from '@/assets/tabNavigator/Play-active.svg'
import PlayWhiteIcon from '@/assets/tabNavigator/Play-white.svg'
import PlayWhite2Icon from '@/assets/tabNavigator/Play-white2.svg'
import PlayInactiveIcon from '@/assets/tabNavigator/Play-inactive.svg'
import ProfileFilledIcon from '@/assets/tabNavigator/Profile--filled.svg'
import ReadFilledIcon from '@/assets/tabNavigator/Read--filled.svg'
import ReadIcon from '@/assets/tabNavigator/Read.svg'
import ProfileIcon from '@/assets/tabNavigator/Profile.svg'
import CrowIcon from '../../assets/crown-black.svg'
import { events } from '@/models/analytics/analytics-events'
import { fontFamily } from '../../theme'
import { BottomTabBarProps } from '@react-navigation/bottom-tabs'

const Icons = {
  [TabRoutes.homeScreen]: [HomeActiveIcon, HomeInactiveIcon],
  [TabRoutes.playScreen]: [PlayActiveIcon, PlayInactiveIcon, PlayWhite2Icon, PlayWhiteIcon],
  [TabRoutes.learningScreen]: [LearnIcon, LearnFilledIcon],
  [TabRoutes.readScreen]: [ReadIcon, ReadFilledIcon],
  [TabRoutes.profileScreen]: [ProfileIcon, ProfileFilledIcon],
  [TabRoutes.membershipScreen]: [CrowIcon, CrowIcon]
} as const
export { Icons as TabBarIcons }

const MapRoutesToEvents = {
  [TabRoutes.homeScreen]: events.bottomNavigation.home,
  [TabRoutes.playScreen]: events.bottomNavigation.play,
  [TabRoutes.learningScreen]: events.bottomNavigation.learn,
  [TabRoutes.readScreen]: events.bottomNavigation.read,
  [TabRoutes.profileScreen]: events.bottomNavigation.profile,
  [TabRoutes.membershipScreen]: events.bottomNavigation.premium
} as const

export const TabBarComponent = observer(({ state, navigation, descriptors }: BottomTabBarProps) => {
  const translateX = useSharedValue(0)
  const store = useStore()
  const {
    config: { enabledTabs }
  } = useTabNavConfig()

  const needsCompleteProfile = store.session.needsCompleteProfile
  const userId = store.session.user?.auth0Id ?? ''
  const joinableNotifications = useQuery(
    api.lounges.queries.getJoinableLoungeNotificationsForUser,
    userId ? { userId } : 'skip'
  )
  const hasJoinableLoungeGame = (joinableNotifications?.length ?? 0) > 0

  // const fetchOnboardingProgress = store.session.fetchOnboardingProgress

  const { width } = useWindowDimensions()
  const [containerWidth, setContainerWidth] = useState(width)

  // TODO: check if this is needed
  // useFocusEffect(
  //   useCallback(() => {
  //     fetchOnboardingProgress()
  //   }, [fetchOnboardingProgress])
  // )

  const enabledRoutes = useMemo(() => {
    return state.routes.filter((route) => {
      const routeName = `${route.name}Screen` as keyof typeof TabRoutes
      return enabledTabs[routeName] ?? false
    })
  }, [state.routes, enabledTabs])
  const focusedTab = useMemo(
    () => enabledRoutes.findIndex((route) => route.name === state.routes[state.index].name),
    [state.index, state.routes[state.index].name, enabledRoutes]
  )

  const tabWidth = useMemo(() => {
    return containerWidth / enabledRoutes.length
  }, [containerWidth, enabledRoutes.length])

  const hasFocusedTab = focusedTab >= 0

  const rnStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: translateX.value }],
      width: tabWidth,
      opacity: hasFocusedTab ? 1 : 0
    }
  }, [tabWidth, hasFocusedTab])

  const prevTabWidth = useRef(tabWidth)

  useEffect(() => {
    if (!hasFocusedTab) return

    const target = focusedTab * tabWidth
    // Snap when the layout changed so a re-measure can't leave the pill mid-animation
    if (prevTabWidth.current !== tabWidth) {
      prevTabWidth.current = tabWidth
      translateX.value = target
      return
    }
    translateX.value = withTiming(target, { duration: 200 })
  }, [focusedTab, hasFocusedTab, tabWidth, translateX])

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width)
  }, [])

  return (
    <View style={styles.container} onLayout={handleLayout}>
      {/* Sliding Indicator */}
      <Animated.View style={[styles.animateView, rnStyle]} />
      {/* Tab Buttons */}
      {enabledRoutes.map((route, index) => {
        const { options } = descriptors[route.key]
        const label = options.title !== undefined ? options.title : route.name

        if (route.name === 'index') {
          return null
        }

        const isFocused = focusedTab === index
        const routeName = `${route.name}Screen` as keyof typeof TabRoutes
        const amplitudeEvent = MapRoutesToEvents[routeName]
        const onPress = () => {
          if (routeName === 'membershipScreen') {
            store.analytics.setEvent(amplitudeEvent, {
              segmentUserId: store.analytics.getSegmentUserID(),
              segmentIdentity: store.analytics.getUserIdentify(),
              from_screen: 'Tab Bar'
            })
          } else {
            store.analytics.setEvent(amplitudeEvent, {
              segmentUserId: store.analytics.getSegmentUserID(),
              segmentIdentity: store.analytics.getUserIdentify()
            })
          }
          const event = navigation.emit({
            type: 'tabPress',
            target: route.key,
            canPreventDefault: true
          })

          if (!(isFocused || event.defaultPrevented)) {
            navigation.navigate({
              name: route.name,
              merge: true,
              params: {}
            })
          }
        }

        const onLongPress = () => {
          navigation.emit({
            type: 'tabLongPress',
            target: route.key
          })
        }

        const Icon = Icons[routeName][isFocused ? 1 : 0]

        const isProfileTab = routeName === TabRoutes.profileScreen
        const isPlayTab = routeName === TabRoutes.playScreen
        return (
          <Pressable
            key={`tab-route-${route.key}`}
            accessibilityRole="button"
            accessibilityState={isFocused ? { selected: true } : {}}
            accessibilityLabel={options.tabBarAccessibilityLabel}
            // testID={options.tabBarTestID}
            onPress={onPress}
            onLongPress={onLongPress}
            style={[styles.item]}
          >
            <View style={styles.iconWrapper}>
              {isProfileTab ? (
                <Avatar size={24} plain={!isFocused} />
              ) : (
                <Icon width={24} height={24} />
              )}
              {isProfileTab && needsCompleteProfile && (
                <DS.Icon name="Dot" color="Utility/Warning" size={10} style={styles.notificationDot} />
              )}
              {isPlayTab && hasJoinableLoungeGame && (
                <DS.Icon name="Dot" color="Utility/Error" size={10} style={styles.notificationDot} />
              )}
            </View>
            <DS.Text variant="Mini/Medium" style={styles.label}>
              {label}
            </DS.Text>
          </Pressable>
        )
      })}
    </View>
  )
})

const styles = StyleSheet.create({
  animateView: {
    height: '100%',
    backgroundColor: `${DS.palette['Brand/Light Grey']}59`,
    zIndex: 0,
    position: 'absolute',
    borderRadius: 40
  },
  container: {
    height: 52,
    flex: 1,
    flexDirection: 'row'
  },
  item: {
    flex: 1,
    gap: 4,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 40,
    paddingVertical: DS.spacing[6]
  },
  iconWrapper: {
    width: 24,
    height: 24
  },
  // Pinned to the icon's top-right corner so it stays put whatever the tab width
  notificationDot: {
    position: 'absolute',
    right: -4,
    top: -2
  },
  // Figma's Mini/Medium is GT America Extended Medium; DS Mini/Medium uses Standard Medium
  label: {
    fontFamily: fontFamily.GTAmericaExtendedMedium
  }
})
